import type { WeeklyReport } from '../game/types';

export type PeriodReportKind = 'quarter' | 'year';

export type PeriodMetricKey =
  | 'revenue'
  | 'operatingProfit'
  | 'netProfit'
  | 'netProfitMargin'
  | 'netCashFlow'
  | 'uniquePassengers'
  | 'seats'
  | 'averageLoadFactor'
  | 'unmetDemand';

export interface PeriodMetricComparison {
  value: number | null;
  change: number | null;
}

export interface PeriodReport {
  period: PeriodReportKind;
  weekStart: number;
  weekEnd: number;
  weeks: number;
  revenue: number;
  operatingCosts: number;
  hubCosts: number;
  operatingProfit: number;
  depreciation: number;
  interest: number;
  principalPaid: number;
  netProfit: number;
  netProfitMargin: number;
  netCashFlow: number;
  uniquePassengers: number;
  segmentPassengers: number;
  seats: number;
  averageLoadFactor: number;
  unmetDemand: number;
  fuelPriceIndex: number;
  comparisons: {
    sequential: Record<PeriodMetricKey, PeriodMetricComparison>;
    yearOverYear: Record<PeriodMetricKey, PeriodMetricComparison>;
  };
}

const PERIOD_WEEKS: Record<PeriodReportKind, number> = {
  quarter: 13,
  year: 52,
};

const METRICS: PeriodMetricKey[] = [
  'revenue',
  'operatingProfit',
  'netProfit',
  'netProfitMargin',
  'netCashFlow',
  'uniquePassengers',
  'seats',
  'averageLoadFactor',
  'unmetDemand',
];

function reportsInRange(reports: WeeklyReport[], weekStart: number, weekEnd: number): WeeklyReport[] {
  return reports.filter((report) => report.week >= weekStart && report.week <= weekEnd).sort((a, b) => a.week - b.week);
}

function aggregate(reports: WeeklyReport[], period: PeriodReportKind, weekStart: number, weekEnd: number): Omit<PeriodReport, 'comparisons'> {
  const sum = (key: keyof WeeklyReport) => reports.reduce((total, report) => total + Number(report[key] ?? 0), 0);
  const seats = sum('seats');
  const segmentPassengers = sum('segmentPassengers');
  return {
    period,
    weekStart,
    weekEnd,
    weeks: reports.length,
    revenue: sum('revenue'),
    operatingCosts: sum('operatingCosts'),
    hubCosts: sum('hubCosts'),
    operatingProfit: sum('operatingProfit'),
    depreciation: sum('depreciation'),
    interest: sum('interest'),
    principalPaid: sum('principalPaid'),
    netProfit: sum('netProfit'),
    netProfitMargin: sum('revenue') > 0 ? sum('netProfit') / sum('revenue') : 0,
    netCashFlow: sum('netCashFlow'),
    uniquePassengers: sum('uniquePassengers'),
    segmentPassengers,
    seats,
    averageLoadFactor: seats > 0 ? Math.min(1, segmentPassengers / seats) : 0,
    unmetDemand: sum('unmetDemand'),
    fuelPriceIndex: reports.length ? sum('fuelPriceIndex') / reports.length : 0,
  };
}

function compare(current: Omit<PeriodReport, 'comparisons'>, baseline: Omit<PeriodReport, 'comparisons'> | null): Record<PeriodMetricKey, PeriodMetricComparison> {
  return METRICS.reduce((result, metric) => {
    const value = baseline?.[metric] ?? null;
    result[metric] = {
      value,
      change: value === null || value === 0 ? null : (current[metric] - value) / Math.abs(value),
    };
    return result;
  }, {} as Record<PeriodMetricKey, PeriodMetricComparison>);
}

function completeAggregate(
  reports: WeeklyReport[],
  period: PeriodReportKind,
  weekStart: number,
  weekEnd: number,
): Omit<PeriodReport, 'comparisons'> | null {
  const periodWeeks = PERIOD_WEEKS[period];
  const selected = reportsInRange(reports, weekStart, weekEnd);
  const requiredWeeks = weekStart === 1 ? periodWeeks - 1 : periodWeeks;
  if (selected.length < requiredWeeks) return null;
  return aggregate(selected, period, weekStart, weekEnd);
}

export function buildPeriodReport(reports: WeeklyReport[], period: PeriodReportKind): PeriodReport | null {
  const periodWeeks = PERIOD_WEEKS[period];
  const sorted = [...reports].sort((a, b) => a.week - b.week);
  const latest = sorted.at(-1);
  if (!latest) return null;

  const completedPeriods = Math.floor(latest.week / periodWeeks);
  if (completedPeriods < 1) return null;

  const weekEnd = completedPeriods * periodWeeks;
  const weekStart = weekEnd - periodWeeks + 1;
  const current = completeAggregate(sorted, period, weekStart, weekEnd);
  if (!current) return null;

  const previous = completeAggregate(sorted, period, weekStart - periodWeeks, weekStart - 1);
  const yearAgo = completeAggregate(sorted, period, weekStart - 52, weekEnd - 52);
  return {
    ...current,
    comparisons: {
      sequential: compare(current, previous),
      yearOverYear: compare(current, yearAgo),
    },
  };
}
